/**
 * Per-day menu-group scheduling — the authored `daysAvailable.perDay` shape and
 * the CONSERVATIVE PROJECTION that keeps every older reader safe.
 *
 * ## Why a projection exists at all
 *
 * `daysAvailable` carries seven day booleans plus a GLOBAL `timeWindows` set, so
 * the day-set and the time-set are a cross product: every window applies to
 * every enabled day. "Mon–Fri 18:00–22:00 AND all day Sat/Sun" is therefore
 * unrepresentable in that shape.
 *
 * `perDay` adds the missing dimension. But readers that predate it — an older
 * POS build (`apps/pos/lib/models/menus/menu_group.dart`, which flattens each
 * window to a `"start|end|active"` string and cannot see extra keys), a stale
 * browser tab, the Go gateway, `modifier_validation_service.dart` — keep
 * evaluating the legacy fields. Those fields are therefore no longer authored:
 * every write recomputes them from `perDay` via `projectLegacyWindows`, chosen
 * so that
 *
 *     legacy(day, minute)  ⟹  perDay(day, minute)
 *
 * for every day and every minute. An older reader can UNDER-serve. It can never
 * OVER-serve. That is structural, not a rollout gate — which matters because the
 * POS OTA tail is unbounded and the permissive failure on an age-restricted
 * group is not recoverable after the fact.
 *
 * ## Why not the obvious alternatives
 *
 * - A day mask on each window: an older POS sees the masks stripped, unions the
 *   windows across all enabled days, and an "all day Saturday" window becomes
 *   all day EVERY day. Permissive. Rejected.
 * - Intersecting the covers: when covers do not intersect the result is empty,
 *   and an empty window collection means ALL DAY to the legacy evaluator — the
 *   maximally permissive inversion of what was intended. Rejected.
 *
 * All functions are pure. Minute resolution throughout, matching
 * `menuGroupAvailability.ts`.
 */

import { readScheduleClock } from "../time/zonedClock";
import {
  MENU_GROUP_DAY_KEYS,
  isTotalPerDay,
  type IMenuGroupDaySchedule,
  type IMenuGroupDaysAvailable,
  type IMenuGroupTimeWindow,
  type MenuGroupDayKey,
} from "./MenuGroup";

export { isTotalPerDay };

/** Minutes since midnight, half-open `[start, end)` at minute resolution. */
export interface IScheduleInterval {
  readonly start: number;
  readonly end: number;
}

/** 00:00 through 23:59 inclusive — every minute of the day. */
export const MINUTES_PER_DAY = 1440;

const FULL_DAY: readonly IScheduleInterval[] = [{ start: 0, end: MINUTES_PER_DAY }];

/** Parse "HH:MM" to minutes since midnight, or null. Mirrors `menuGroupAvailability.parseTimeToMinutes`. */
function parseTimeToMinutes(s: string | undefined): number | null {
  if (!s) return null;
  const parts = s.split(":");
  if (parts.length !== 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isInteger(h) || !Number.isInteger(m) || h < 0 || h > 23 || m < 0 || m > 59) {
    return null;
  }
  return h * 60 + m;
}

function parseWindow(w: IMenuGroupTimeWindow): { start: number; end: number } | null {
  if (w.isActive === false) return null;
  const start = parseTimeToMinutes(w.startTime);
  const end = parseTimeToMinutes(w.endTime);
  if (start == null || end == null) return null;
  return { start, end };
}

/**
 * The part of a window that falls on ITS OWN day. Overnight windows (start >
 * end) belong to the day they START: 22:00–02:00 on Monday is Monday 22:00–23:59
 * here, and Tuesday 00:00–02:00 via `windowTail`. Both ends are inclusive at
 * minute resolution, so `end` becomes `end + 1`.
 */
function windowOwnDay(w: IMenuGroupTimeWindow): IScheduleInterval[] {
  const p = parseWindow(w);
  if (p == null) return [];
  if (p.start <= p.end) return [{ start: p.start, end: p.end + 1 }];
  return [{ start: p.start, end: MINUTES_PER_DAY }];
}

/** The after-midnight tail an overnight window carries into the NEXT day. */
function windowTail(w: IMenuGroupTimeWindow): IScheduleInterval[] {
  const p = parseWindow(w);
  if (p == null || p.start <= p.end) return [];
  return [{ start: 0, end: p.end + 1 }];
}

/** Merge overlapping/adjacent intervals into a canonical, sorted, disjoint cover. */
function normalizeIntervals(intervals: readonly IScheduleInterval[]): IScheduleInterval[] {
  const sorted = [...intervals]
    .filter((i) => i.end > i.start)
    .sort((a, b) => a.start - b.start || a.end - b.end);
  const out: IScheduleInterval[] = [];
  for (const cur of sorted) {
    const last = out[out.length - 1];
    if (last != null && cur.start <= last.end) {
      if (cur.end > last.end) out[out.length - 1] = { start: last.start, end: cur.end };
    } else {
      out.push({ start: cur.start, end: cur.end });
    }
  }
  return out;
}

/** Total minutes covered. */
function coverSize(cover: readonly IScheduleInterval[]): number {
  return cover.reduce((sum, i) => sum + (i.end - i.start), 0);
}

/** True when `outer` fully contains `inner`. */
function covers(
  outer: readonly IScheduleInterval[],
  inner: readonly IScheduleInterval[],
): boolean {
  return inner.every((need) =>
    outer.some((have) => have.start <= need.start && have.end >= need.end),
  );
}

function intervalsEqual(
  a: readonly IScheduleInterval[],
  b: readonly IScheduleInterval[],
): boolean {
  return (
    a.length === b.length && a.every((x, i) => x.start === b[i]!.start && x.end === b[i]!.end)
  );
}

/**
 * The minutes a day's OWN schedule opens on that day — overnight tails
 * excluded, because they land on the next day (see `weeklyCovers`).
 *
 *   ∅         — the day is off, or every window is inactive/unparseable
 *   FULL_DAY  — the day is on with NO windows (empty ⇒ all day, as always)
 *   segments  — the union of the day's active, parseable windows
 */
export function coverForDay(day: IMenuGroupDaySchedule): IScheduleInterval[] {
  if (!day.isAvailable) return [];
  if (day.windows.length === 0) return [...FULL_DAY];
  return normalizeIntervals(day.windows.flatMap(windowOwnDay));
}

/**
 * 🔴 AN ALL-DAY DAY RUNS UNTIL THE GROUP'S LATEST OVERNIGHT CLOSE.
 *
 * The last minute (inclusive) that any open day's active overnight window
 * reaches into the next day, or null when the week has no overnight window.
 * An all-day day carries into the next morning until this minute.
 *
 * House Of Pho's Dinner is "Mon–Fri 6 PM–4 AM, all day Sat and Sun": the owner
 * keeps a 4 AM day, and Lunch picks up at 4 AM. With all day stopping at
 * midnight, Monday 12–4 AM was covered by nothing — Sunday's all day had ended
 * and Monday's dinner had not begun — and the till showed an empty Dine-In
 * menu on Sunday night (2026-10-12). "All day Sunday" means all of Sunday's
 * service, and the group's own late close is where its service ends.
 *
 * Bounded by what the operator already scheduled for THIS group: a group with
 * no overnight hours still ends all day at midnight, so a "Sat all day, Sun
 * closed" alcohol group cannot start selling into Sunday from this rule.
 */
export function allDayCarryEnd(
  perDay: Readonly<Record<MenuGroupDayKey, IMenuGroupDaySchedule>>,
): number | null {
  let latest: number | null = null;
  for (const key of MENU_GROUP_DAY_KEYS) {
    const day = perDay[key];
    if (!day.isAvailable) continue;
    for (const w of day.windows) {
      const p = parseWindow(w);
      if (p == null || p.start <= p.end) continue;
      if (latest == null || p.end > latest) latest = p.end;
    }
  }
  return latest;
}

/**
 * The after-midnight minutes a day carries into the next day: its overnight
 * windows' tails, or — for an all-day day — through `carryEnd`.
 */
function tailForDay(day: IMenuGroupDaySchedule, carryEnd: number | null): IScheduleInterval[] {
  if (!day.isAvailable) return [];
  if (day.windows.length === 0) return carryEnd == null ? [] : [{ start: 0, end: carryEnd + 1 }];
  return normalizeIntervals(day.windows.flatMap(windowTail));
}

function coversOfWeek(
  perDay: Readonly<Record<MenuGroupDayKey, IMenuGroupDaySchedule>>,
  carryEnd: number | null,
): Record<MenuGroupDayKey, IScheduleInterval[]> {
  return Object.fromEntries(
    MENU_GROUP_DAY_KEYS.map((key, i) => {
      const previous = MENU_GROUP_DAY_KEYS[(i + 6) % 7]!;
      return [
        key,
        normalizeIntervals([...coverForDay(perDay[key]), ...tailForDay(perDay[previous], carryEnd)]),
      ];
    }),
  ) as Record<MenuGroupDayKey, IScheduleInterval[]>;
}

/**
 * The minutes each calendar day is ACTUALLY open: its own cover plus what the
 * previous day carries past midnight (overnight tails, and an all-day day's
 * carry — see `allDayCarryEnd`). This is exactly what `isCurrentDayAllowed`
 * evaluates, minute for minute, so the uniformity check and every "is it open,
 * until when" reader reason about the same week the tills do.
 */
export function weeklyCovers(
  perDay: Readonly<Record<MenuGroupDayKey, IMenuGroupDaySchedule>>,
): Record<MenuGroupDayKey, IScheduleInterval[]> {
  return coversOfWeek(perDay, allDayCarryEnd(perDay));
}

/** True when `now`'s minute, on the restaurant's clock, falls inside the cover. */
export function coverContains(
  cover: readonly IScheduleInterval[],
  now: Date,
  timeZone?: string | null,
): boolean {
  const minute = readScheduleClock(now, timeZone).minute;
  return cover.some((i) => minute >= i.start && minute < i.end);
}

/**
 * The effective schedule for one day, from `perDay` when present and from the
 * legacy day-boolean + global-window shape otherwise. This is the ONE reader
 * every consumer should use so the two shapes cannot drift.
 */
export function resolveDaySchedule(
  da: IMenuGroupDaysAvailable | undefined,
  dayKey: MenuGroupDayKey,
): IMenuGroupDaySchedule {
  if (da == null || !da.isActive) return { isAvailable: true, windows: [] };
  // Totality, not mere presence — the same gate `isCurrentDayAllowed` applies.
  // If these two disagreed, a malformed document would be read one way by the
  // POS and another by the marketplace transformers that call this.
  if (isTotalPerDay(da.perDay)) return da.perDay![dayKey];

  const isAvailable = da[dayKey] ?? true;
  const windows = da.timeWindows != null ? Object.values(da.timeWindows) : [];
  if (windows.length > 0) return { isAvailable, windows };

  // Legacy single-window pair. Both present ⇒ one window; otherwise no restriction.
  const start = parseTimeToMinutes(da.startTime);
  const end = parseTimeToMinutes(da.endTime);
  if (start != null && end != null) {
    return {
      isAvailable,
      windows: [{ startTime: da.startTime!, endTime: da.endTime!, isActive: true }],
    };
  }
  return { isAvailable, windows: [] };
}

/**
 * The legacy re-expression of `perDay`: every day whose own cover is non-empty
 * switched on, all of them carrying the first such day's windows.
 */
function legacyCandidate(
  perDay: Readonly<Record<MenuGroupDayKey, IMenuGroupDaySchedule>>,
): { days: Record<MenuGroupDayKey, boolean>; windows: IMenuGroupTimeWindow[] } {
  const days = Object.fromEntries(
    MENU_GROUP_DAY_KEYS.map((k) => [k, coverForDay(perDay[k]).length > 0]),
  ) as Record<MenuGroupDayKey, boolean>;
  const first = MENU_GROUP_DAY_KEYS.find((k) => days[k]);
  const windows = first != null ? perDay[first].windows.filter((x) => x.isActive !== false) : [];
  return { days, windows };
}

/**
 * True when the schedule is expressible in the legacy shape with NO loss.
 * Callers OMIT `perDay` entirely in that case, which is what keeps existing
 * documents byte-identical.
 *
 * Decided by VERIFICATION, not by comparing day covers: build the legacy
 * re-expression and require its week to equal the authored week, minute for
 * minute. Once overnight windows carry into the next day, "every available day
 * has the same cover" stops implying that — a day switched off can still be
 * open after midnight on its predecessor's tail, and re-expressing it with its
 * own evening window would OVER-serve.
 */
export function isUniformAcrossDays(
  perDay: Readonly<Record<MenuGroupDayKey, IMenuGroupDaySchedule>> | undefined,
): boolean {
  if (perDay == null) return true;
  const { days, windows } = legacyCandidate(perDay);
  const reexpressed = {} as Record<MenuGroupDayKey, IMenuGroupDaySchedule>;
  for (const k of MENU_GROUP_DAY_KEYS) reexpressed[k] = { isAvailable: days[k], windows };
  const authored = weeklyCovers(perDay);
  const legacy = weeklyCovers(reexpressed);
  return MENU_GROUP_DAY_KEYS.every((k) => intervalsEqual(authored[k], legacy[k]));
}

export interface ILegacyProjection {
  readonly days: Record<MenuGroupDayKey, boolean>;
  readonly timeWindows: Record<string, IMenuGroupTimeWindow>;
}

function minutesToHHMM(m: number): string {
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

/**
 * Project `perDay` down to the legacy `{ days, timeWindows }` shape such that
 * `legacy(d,t) ⟹ perDay(d,t)` for every day `d` and minute `t`.
 *
 *   1. E = { d : cover(d) ≠ ∅ }; every d ∉ E projects to false
 *   2. E = ∅ → all days false, timeWindows {} (never available)
 *   3. dPrimary = the SMALLEST cover in E, ties broken by MENU_GROUP_DAY_KEYS order
 *   4. W = cover(dPrimary); a full-day W renders as {} (legacy "all day")
 *   5. day[d] = (d ∈ E) AND cover(d) ⊇ W          ← the narrowing step
 *
 * Step 5 is why the day booleans must be DERIVED rather than authored: it turns
 * days OFF that the operator left on, whenever those days do not fully contain
 * the projected window. Any design that keeps the booleans as the outer gate
 * cannot represent it.
 *
 * Window ids are positional and stable (`lp-0`, `lp-1`) — never `Date.now()` —
 * so an unchanged schedule re-projects to a byte-identical map and does not
 * churn the POS Hive cache or trip its dirty-check.
 */
export function projectLegacyWindows(
  perDay: Readonly<Record<MenuGroupDayKey, IMenuGroupDaySchedule>>,
): ILegacyProjection {
  // The WEEK's covers, overnight tails included. The projected windows are
  // plain same-day intervals (never overnight), so an old reader's legacy
  // evaluation of them is the same under either overnight rule.
  //
  // WITHOUT the all-day carry, deliberately. The carry only ever adds minutes,
  // so a projection of the narrower week is still ⊆ perDay — and it keeps every
  // stored projection byte-identical, which `projectionMatches` (a schema
  // refinement) requires of documents written before the carry existed.
  const week = coversOfWeek(perDay, null);
  const coversByDay = new Map<MenuGroupDayKey, IScheduleInterval[]>();
  for (const key of MENU_GROUP_DAY_KEYS) coversByDay.set(key, week[key]);

  const enabled = MENU_GROUP_DAY_KEYS.filter((k) => coversByDay.get(k)!.length > 0);

  const days = Object.fromEntries(
    MENU_GROUP_DAY_KEYS.map((k) => [k, false]),
  ) as Record<MenuGroupDayKey, boolean>;

  if (enabled.length === 0) return { days, timeWindows: {} };

  let primary = enabled[0]!;
  let primarySize = coverSize(coversByDay.get(primary)!);
  for (const key of enabled) {
    const size = coverSize(coversByDay.get(key)!);
    if (size < primarySize) {
      primary = key;
      primarySize = size;
    }
  }

  const w = coversByDay.get(primary)!;
  for (const key of enabled) {
    days[key] = covers(coversByDay.get(key)!, w);
  }

  // A full-day projection renders as {} — the legacy evaluator reads an empty
  // window collection as "no time restriction", which is exactly right here.
  const isFullDay = w.length === 1 && w[0]!.start === 0 && w[0]!.end === MINUTES_PER_DAY;
  if (isFullDay) return { days, timeWindows: {} };

  const timeWindows: Record<string, IMenuGroupTimeWindow> = {};
  w.forEach((interval, i) => {
    timeWindows[`lp-${i}`] = {
      startTime: minutesToHHMM(interval.start),
      // `end` is exclusive internally; the stored form is inclusive.
      endTime: minutesToHHMM(Math.max(interval.start, interval.end - 1)),
      isActive: true,
    };
  });
  return { days, timeWindows };
}

/**
 * Whether the stored legacy fields equal the projection of the stored `perDay`.
 * Used by the schema refinement so a caller cannot persist authored data and a
 * stale projection together — divergence has to be unrepresentable, not merely
 * discouraged.
 *
 * The `perDay == null` early return is load-bearing: a document without an
 * authored schedule has no projection to match, and treating that as a mismatch
 * would refuse every menu group in production. The schema refinement carries the
 * same guard, so a negative control must remove BOTH to go red.
 */
export function projectionMatches(da: IMenuGroupDaysAvailable): boolean {
  if (da.perDay == null) return true;
  const expected = projectLegacyWindows(da.perDay);
  for (const key of MENU_GROUP_DAY_KEYS) {
    if ((da[key] ?? false) !== expected.days[key]) return false;
  }
  const actual = da.timeWindows ?? {};
  const actualKeys = Object.keys(actual).sort();
  const expectedKeys = Object.keys(expected.timeWindows).sort();
  if (actualKeys.length !== expectedKeys.length) return false;
  return expectedKeys.every((k, i) => {
    if (actualKeys[i] !== k) return false;
    const a = actual[k];
    const e = expected.timeWindows[k]!;
    return (
      a != null &&
      a.startTime === e.startTime &&
      a.endTime === e.endTime &&
      (a.isActive ?? true) === true
    );
  });
}

/**
 * Build a complete `daysAvailable` for writing: authored `perDay` plus its
 * projection, or the legacy shape alone when the schedule is uniform. This is
 * the ONLY supported way to construct the field for persistence.
 */
export function buildDaysAvailableForWrite(
  base: IMenuGroupDaysAvailable,
  perDay: Readonly<Record<MenuGroupDayKey, IMenuGroupDaySchedule>> | undefined,
): IMenuGroupDaysAvailable {
  if (perDay == null || isUniformAcrossDays(perDay)) {
    // Uniform ⇒ legacy-only. Strip any stale `perDay` so the two cannot disagree.
    const { perDay: _drop, ...rest } = base;
    if (perDay == null) return rest;
    const { days, windows } = legacyCandidate(perDay);
    const timeWindows: Record<string, IMenuGroupTimeWindow> = {};
    windows.forEach((x, i) => {
      timeWindows[`lp-${i}`] = { startTime: x.startTime, endTime: x.endTime, isActive: true };
    });
    return { ...rest, ...days, timeWindows };
  }
  const projection = projectLegacyWindows(perDay);
  return { ...base, ...projection.days, timeWindows: projection.timeWindows, perDay };
}
